/**
 * Who is sitting on something, and the sentence to send them.
 *
 * The board has always been able to say a piece has been with a client nine
 * days. Nobody chased, because chasing is never the most urgent thing on any
 * morning and the number just went up. What was missing was not a better
 * display of the wait — it was the message, written, with one button.
 *
 * This card does not send anything. The studio's own WhatsApp thread is where
 * these conversations already live, and a platform that started messaging
 * clients by itself would be a second voice in it. So: copy, send it where
 * you were going to send it anyway, and tell this that you did — which is the
 * only part it needs, because that is what stops it asking again tomorrow and
 * what the month recap counts.
 */

import { Check, Copy, MessageSquareWarning } from 'lucide-react'
import { useState } from 'react'
import { BentoCard } from './BentoCard'
import { Button } from './Button'
import { useToast } from '../context/ToastContext'
import { api, apiErrorMessage } from '../lib/api'
import { useApi } from '../useApi'
import { cheer, DID } from '../lib/hapiMood'

interface Waiting {
  piece_id: string
  title: string
  days_waiting: number
  nudge_count: number
  nudged_at: string | null
  scheduled_for: string | null
  overdue: boolean
}

interface Chase {
  client_id: string
  client_name: string
  contact_name: string | null
  has_live_link: boolean
  pieces: Waiting[]
  message: string
}

export function Chasing({ delay = 0, className }: { delay?: number; className?: string }) {
  const { result, reload } = useApi<Chase[]>('/nudges')
  const { showToast } = useToast()
  const [copied, setCopied] = useState<string | null>(null)
  const [marking, setMarking] = useState<string | null>(null)

  // Nothing to chase is the good outcome, and a card saying so every day is a
  // card people learn to look past. It goes away instead.
  if (result.state !== 'loaded' || result.data.length === 0) return null
  const chases = result.data

  async function copy(chase: Chase) {
    try {
      await navigator.clipboard.writeText(chase.message)
      setCopied(chase.client_id)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      showToast('Could not reach the clipboard. Select the text and copy it.', 'warning')
    }
  }

  async function markSent(chase: Chase) {
    setMarking(chase.client_id)
    try {
      const { marked } = await api.post<{ marked: number }>('/nudges/sent', {
        pieceIds: chase.pieces.map((p) => p.piece_id),
      })
      if (marked > 0) cheer(DID['chased']!)
      showToast(
        marked === 0
          ? `${chase.client_name} has already answered — nothing left to chase.`
          : `Noted. ${chase.client_name} will not come up again for a few days.`,
      )
      reload()
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setMarking(null)
    }
  }

  return (
    <BentoCard
      title="Waiting on a client"
      description="Three days or more, and nobody has asked"
      delay={delay}
      {...(className === undefined ? {} : { className })}
    >
      <div className="flex flex-col gap-4">
        {chases.map((chase) => (
          <div key={chase.client_id} className="rounded-xl border border-canvas-fg/10 p-3.5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-display text-sm font-semibold text-canvas-fg">
                {chase.client_name}
                {chase.contact_name !== null && (
                  <span className="ml-1.5 text-xs font-medium text-canvas-fg/55">{chase.contact_name}</span>
                )}
              </p>
              {/*
                * Chased twice already is a different situation from never
                * chased, and the person about to type a third message should
                * be told so before they type it.
                */}
              {chase.pieces.some((p) => p.nudge_count > 0) && (
                <span className="text-[11px] font-medium text-canvas-fg/55">
                  asked {Math.max(...chase.pieces.map((p) => p.nudge_count))}×
                </span>
              )}
            </div>

            <ul className="mt-2 flex flex-col gap-1">
              {chase.pieces.map((p) => (
                <li key={p.piece_id} className="flex items-start gap-2 text-xs text-canvas-fg/75">
                  <MessageSquareWarning
                    size={13}
                    className={p.overdue ? 'mt-0.5 shrink-0 text-danger-500' : 'mt-0.5 shrink-0 text-canvas-fg/35'}
                  />
                  <span className="min-w-0 break-words">
                    {p.title} — {p.days_waiting === 0 ? 'today' : `${p.days_waiting}d`}
                    {p.overdue && <span className="text-danger-500"> · was due out</span>}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-2.5 rounded-lg bg-canvas-fg/[0.04] px-3 py-2 text-xs italic text-canvas-fg/70">
              {chase.message}
            </p>

            <div className="mt-2.5 flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={() => void copy(chase)}>
                {copied === chase.client_id ? <Check size={13} className="mr-1.5" /> : <Copy size={13} className="mr-1.5" />}
                {copied === chase.client_id ? 'Copied' : 'Copy message'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={marking === chase.client_id}
                onClick={() => void markSent(chase)}
              >
                I&rsquo;ve asked them
              </Button>
            </div>
          </div>
        ))}
      </div>
    </BentoCard>
  )
}
